import apiClient from "./apiClient";
import { getOrderSaleLineItems } from "./salesOrderReceiptUtils";

export function hasZatcaApp(purchasedApps) {
  return purchasedApps?.some(
    (app) => app.PType === "ZATCA" && app.PStatus === "Active"
  );
}

export function isZatcaConfigured(zatcaMetadata) {
  return Boolean(
    zatcaMetadata?.vatNumber &&
      zatcaMetadata?.crNumber &&
      zatcaMetadata?.name
  );
}

export function isZatcaActive(purchasedApps, zatcaMetadata) {
  return hasZatcaApp(purchasedApps) && isZatcaConfigured(zatcaMetadata);
}

export function formatZatcaMetadata(metadata) {
  if (!metadata) return null;

  let vatNumber = metadata.vatNumber || "";
  if (vatNumber.length !== 15) {
    vatNumber =
      vatNumber.length < 15
        ? `3${vatNumber.padEnd(13, "0")}3`
        : vatNumber.substring(0, 15);
  }

  let buildingNumber = metadata.address?.buildingNumber || "";
  if (buildingNumber.length !== 4) {
    buildingNumber =
      buildingNumber.length < 4
        ? buildingNumber.padStart(4, "0")
        : buildingNumber.substring(0, 4);
  }

  return {
    tenantId: metadata.tenantId,
    name: metadata.name || "",
    vatNumber,
    crNumber: metadata.crNumber || "",
    address: {
      street: metadata.address?.street || "",
      buildingNumber,
      district: metadata.address?.district || "",
      city: metadata.address?.city || "",
      postalCode: metadata.address?.postalCode || "",
      country: metadata.address?.country || "SA",
    },
  };
}

export function validateZatcaMetadata(metadata) {
  const errors = [];
  if (!metadata?.vatNumber) errors.push("VAT number is required");
  if (!metadata?.crNumber) errors.push("CR Number is required");
  if (!metadata?.name) errors.push("Seller name is required");

  return {
    isValid: errors.length === 0,
    errors,
    metadata: formatZatcaMetadata(metadata),
  };
}

export function normalizeZatcaResponse(response) {
  if (!response || typeof response !== "object") return response;

  const qrCodeData =
    response.qrCodeData ||
    response.qrCode ||
    response.qr ||
    response.qrSellertStatus ||
    response.qrSellerStatus ||
    null;

  return { ...response, qrCodeData };
}

/** Merge duplicate sold lines and exclude returned rows for ZATCA invoice display. */
export function consolidateZatcaLineItems(lineItems = []) {
  const merged = new Map();

  for (const item of lineItems) {
    if (item?.returned) continue;

    const name = item.productName || item.name || "Item";
    const taxRate = Number(item.tax || item.taxRate || 15);
    const unitPrice = parseFloat(item.price || 0) || 0;
    const quantity = parseFloat(item.quantity || item.units || 0) || 0;
    if (quantity <= 0) continue;

    const key = `${item.productId || name}|${unitPrice}|${taxRate}`;

    if (merged.has(key)) {
      merged.get(key).quantity += quantity;
    } else {
      merged.set(key, {
        ...item,
        productName: name,
        name,
        price: unitPrice,
        quantity,
        tax: taxRate,
      });
    }
  }

  return Array.from(merged.values());
}

export function resolveZatcaLineItems(order, lineItems) {
  const source = lineItems?.length
    ? lineItems.filter((item) => !item?.returned)
    : getOrderSaleLineItems(order);
  return consolidateZatcaLineItems(source);
}

export function calculateZatcaTotals(lineItems = []) {
  let totalExcludingVAT = 0;
  let totalVAT = 0;

  const items = lineItems.map((item, index) => {
    let taxRate = Number(item.tax || item.taxRate || 15);
    if (taxRate !== 5 && taxRate !== 15) taxRate = 15;

    const quantity = parseFloat(item.quantity || item.units || 1) || 1;
    const unitPrice = parseFloat(item.price || 0) || 0;
    const itemTotal = unitPrice * quantity;
    const itemVAT = (itemTotal * taxRate) / 100;

    totalExcludingVAT += itemTotal;
    totalVAT += itemVAT;

    return {
      id: index + 1,
      name: item.productName || item.name || `Item ${index + 1}`,
      quantity,
      unitPrice,
      taxRate,
      totalExcludingVAT: Number(itemTotal.toFixed(2)),
    };
  });

  return {
    totalExcludingVAT: Number(totalExcludingVAT.toFixed(2)),
    totalVAT: Number(totalVAT.toFixed(2)),
    totalIncludingVAT: Number((totalExcludingVAT + totalVAT).toFixed(2)),
    items,
  };
}

function mapPaymentMethod(order) {
  const method = String(order?.paymentType || order?.paymentMethod || "cash").toLowerCase();
  if (method.includes("card")) return "CARD";
  if (method.includes("bank")) return "BANK";
  if (order?.paid === false) return "CREDIT";
  return "CASH";
}

function parseOrderDate(order) {
  const raw = order?.createdAt;
  if (typeof raw === "string" && raw.includes(".")) {
    const parts = raw.split(".");
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    const hour = parseInt(parts[3], 10) || 0;
    const minute = parseInt(parts[4], 10) || 0;
    const second = parseInt(parts[5], 10) || 0;
    if (Number.isFinite(year)) {
      return new Date(year, month, day, hour, minute, second);
    }
  }

  const parsed = new Date(raw || Date.now());
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
}

export async function generateZatcaInvoiceFromOrder({
  order,
  lineItems,
  zatcaMetadata,
  invoicePrefix = "SO",
  silent = false,
}) {
  const validation = validateZatcaMetadata(zatcaMetadata);
  if (!validation.isValid) {
    throw new Error(validation.errors.join(", "));
  }

  const itemsSource = resolveZatcaLineItems(order, lineItems);
  if (!itemsSource.length) {
    throw new Error("No billable items found for ZATCA invoice");
  }
  const totals = calculateZatcaTotals(itemsSource);
  const orderDate = parseOrderDate(order);
  const orderId = order?.orderId || order?.id || Date.now();
  const invoiceNumber = `${invoicePrefix}-${orderDate.getFullYear()}-${String(orderId).padStart(6, "0")}`;

  const billData = {
    invoiceDetails: {
      invoiceNumber,
      issueDate: orderDate.toISOString().split("T")[0],
      issueTime: orderDate.toTimeString().split(" ")[0],
      previousInvoiceHash:
        "NWZlY2ViNjZmZmM4NmYzOGQ5NTI3ODZjNmQ2OTZjNzljMmRiYzIzOWRkNGU5MWI0NjcyOWQ3M2EyN2ZiNTdlOQ==",
    },
    seller: validation.metadata,
    items: totals.items,
    totals: {
      totalExcludingVAT: totals.totalExcludingVAT,
      totalVAT: totals.totalVAT,
      totalIncludingVAT: totals.totalIncludingVAT,
    },
    payment: {
      method: mapPaymentMethod(order),
    },
  };

  const url = `https://api-pos.hulmsolutions.com/api/fatoora/${validation.metadata.tenantId}/process-json`;
  const result = await apiClient.post(url, billData, {
    context: "ZATCA E-Invoice",
    silent,
  });

  return {
    billData,
    zatcaResponse: normalizeZatcaResponse(result),
  };
}

export function formatZatcaMoney(amount) {
  return (Number(amount || 0) / 100).toFixed(2);
}
